import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaExclamationTriangle } from 'react-icons/fa';
import {
  PendingResult,
  subscribeOutbox,
  startOutboxRunner,
  flushOutbox,
} from '../services/resultOutbox';

/**
 * Persistent reminder that a match result has not reached the server yet, and
 * the host for the retry runner.
 *
 * Mounted app-wide on purpose: the result must keep retrying after the player
 * has walked away from the winner screen, closed the app, or reloaded. Without
 * a visible reminder an unsent result is invisible until an admin notices the
 * match is still open.
 */

const Bar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9000;
  background: rgba(120, 20, 20, 0.96);
  color: #fff;
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 14px;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.4);
`;

const Msg = styled.div`
  flex: 1;
  line-height: 1.3;
`;

const RetryBtn = styled.button`
  background: #fff;
  color: #7a1414;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-family: 'Roboto Condensed', sans-serif;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`;

const UnsentResultBanner: React.FC = () => {
  const [pending, setPending] = useState<PendingResult[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const stopRunner = startOutboxRunner();
    const unsub = subscribeOutbox(setPending);
    return () => {
      stopRunner();
      unsub();
    };
  }, []);

  if (pending.length === 0) return null;

  const handleRetry = async () => {
    setSending(true);
    await flushOutbox().catch(() => {});
    setSending(false);
  };

  const first = pending[0];

  return (
    <Bar role="alert">
      <FaExclamationTriangle />
      <Msg>
        {pending.length === 1
          ? `Result not sent yet: ${first.label}.`
          : `${pending.length} results not sent yet.`}{' '}
        Retrying automatically — keep this app open when you have signal.
      </Msg>
      <RetryBtn onClick={handleRetry} disabled={sending}>
        {sending ? 'Sending…' : 'Retry now'}
      </RetryBtn>
    </Bar>
  );
};

export default UnsentResultBanner;
